import wixIcon from 'assets/images/integrations/wix.svg';

import type { ConversionRule } from 'modules/pixel/pixelApiTypes';

import { CodeBlock } from '../CodeBlock';
import type { IntegrationContext, IntegrationSnippet, PixelIntegration } from './types';

/**
 * Wix — install the universal pixel via Custom Code in Site Settings. Note: Custom Code is
 * gated behind paid Wix plans; Free/Light plans cannot inject scripts.
 */
export const wixIntegration: PixelIntegration = {
  id: 'wix',
  label: 'Wix',
  description:
    'Install via Settings → Custom Code on all pages. Requires a paid Wix plan — Free/Light plans block custom scripts.',
  icon: <img src={wixIcon} alt="" className="h-4 w-4" />,
  docsHref: 'https://support.wix.com/en/article/about-custom-code',
  renderSetup: (ctx) => (
    <div className="flex flex-col gap-4">
      <Step number={1} title="Confirm you're on a Premium Wix plan">
        <p className="text-base-xs text-primary-gray-500">
          Custom Code is only available on paid plans. If you&apos;re on Free or Light, you&apos;ll
          need to upgrade or use a Wix App Market pixel app instead.
        </p>
      </Step>
      <Step number={2} title="Open Settings → Custom Code">
        <p className="text-base-xs text-primary-gray-500">
          In Wix dashboard: <strong>Settings → Advanced → Custom Code → + Add Custom Code</strong>.
        </p>
      </Step>
      <Step number={3} title="Paste the universal pixel and configure">
        <p className="mb-2 text-base-xs text-primary-gray-500">In the Custom Code panel:</p>
        <ul className="ml-5 list-disc space-y-1 text-base-xs text-primary-gray-600">
          <li>
            <strong>Code snippet</strong>: paste the script below
          </li>
          <li>
            <strong>Name</strong>: <code>CoolAI Universal Pixel</code>
          </li>
          <li>
            <strong>Add Code to Pages</strong>: <em>All pages</em>, with{' '}
            <em>Load code on each new page</em>
          </li>
          <li>
            <strong>Place Code in</strong>: <em>Head</em> (Wix doesn&apos;t allow body-tag
            injection)
          </li>
        </ul>
        <div className="mt-2">
          <CodeBlock language="html" code={ctx.pixelScriptTag} />
        </div>
      </Step>
      <Step number={4} title="(Optional) Fire from Velo for custom events">
        <p className="text-base-xs text-primary-gray-500">
          For events not reachable by the trigger system (Wix backend events, form submits via
          Velo), use the per-rule snippet below inside a{' '}
          <code className="rounded bg-primary-gray-100 px-1 font-mono text-[11px]">
            $w.onReady(...)
          </code>{' '}
          or front-end event handler.
        </p>
      </Step>
    </div>
  ),
  buildSnippetForRule: (rule, ctx) => buildWixSnippet(rule, ctx),
};

function Step({
  number,
  title,
  children,
}: {
  number: number;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-2">
        <span className="flex h-5 w-5 items-center justify-center rounded-full bg-primary-electric-100 text-[11px] font-semibold text-primary-electric-700">
          {number}
        </span>
        <h4 className="text-base-sm font-medium text-primary-gray-900">{title}</h4>
      </div>
      <div className="pl-7">{children}</div>
    </div>
  );
}

function buildWixSnippet(rule: ConversionRule, ctx: IntegrationContext): IntegrationSnippet {
  const lines: string[] = [`  'category': ${JSON.stringify(rule.category)}`];

  if (rule.subcategory) lines.push(`  'subcategory': ${JSON.stringify(rule.subcategory)}`);
  const extractorKeys = rule.extractors ? Object.keys(rule.extractors) : [];

  for (const key of extractorKeys) {
    const placeholder =
      key === 'amount'
        ? '\'<order total>\''
        : key === 'currency'
          ? '\'USD\''
          : key === 'order_number'
            ? '\'<order id>\''
            : '\'\'';

    lines.push(`  '${key}': ${placeholder}`);
  }
  const body = lines.join(',\n');

  const code = [
    '<script>',
    'if (window.CMPIXEL && CMPIXEL.fire) {',
    `  CMPIXEL.fire('${ctx.pixelCode}', {`,
    body,
    '  });',
    '}',
    '</script>',
  ].join('\n');

  return {
    code,
    language: 'html',
    label: 'Paste into a per-page Custom Code block',
    note: 'For per-page Custom Code, set "Add Code to Pages" to the specific page (e.g. /thank-you) instead of "All pages".',
  };
}
